import * as React from 'react'
import { FlatList, Platform, SectionList, Text, TouchableOpacity, View, SectionListData } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Select from '@components/select/Select'
import Modal from '@components/modal/Modal'
import Drawer from '@components/drawer/Drawer'
import Icon from '@components/icon/Icons'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'
import Checkbox from '@components/checkbox/Checkbox'

import sanitize from '@utils/Sanitizer'
import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

interface Option {
  value: string
  label: string
}

interface SpaceSection {
  title: string
  data: Option[]
  selectAllEnabled?: boolean
}

interface Props {
  children: React.ReactNode
  title: string
  subtitle: string
  sections: SpaceSection[]
  value: string[]
  onItemSelected: (item: string[]) => void
}

const SearchModal = ({ sections, onItemSelected, value, subtitle, title }: Omit<Props, 'children'>) => {
  const i18n = useI18n()

  const [search, setSearch] = React.useState('')
  const [selected, setSelected] = React.useState(value)
  const [, bp] = useBreakpoint()

  const items = React.useMemo(
    () =>
      sections.map((s) => ({
        ...s,
        data: [{ items: s.data.filter((d) => sanitize(d.label).includes(sanitize(search))) }],
      })),
    [sections, search]
  )

  const onSubmit = () => {
    onItemSelected(selected || '')
    Modal.close()
  }

  const onSelected = (val: string) => {
    const active = selected.includes(val)

    if (active) {
      setSelected(selected.filter((s) => s !== val))
    } else {
      setSelected([...selected, val])
    }
  }

  const onSelectAll = (allSelected: boolean, val: string[]) => {
    if (allSelected) {
      setSelected(selected.filter((s) => !val.includes(s)))
    } else {
      setSelected([...selected, ...val.filter((s) => !selected.includes(s))])
    }
  }

  const renderItem = ({ item, index }: { item: Option; index: number }) => {
    const focused = selected.includes(item.value)

    return (
      <SelectContainer key={item.value} half={index % 2 === 0}>
        <Select
          label={item.label}
          onClick={() => onSelected(item.value)}
          focused={focused}
          selectStyle={!focused ? 'empty' : 'default'}
        />
      </SelectContainer>
    )
  }

  const renderSection = ({ item }: { item: { items: Option[] } }) => {
    if (Platform.OS === 'web') {
      return <Items>{item.items.map((item, index) => renderItem({ item, index }))}</Items>
    }

    return <FlatList data={item.items} numColumns={2} renderItem={renderItem} keyExtractor={(it) => it.value} />
  }

  const renderSectionHeader = ({
    section,
  }: {
    section: SectionListData<
      {
        items: Option[]
      },
      {
        data: {
          items: Option[]
        }[]
        title: string
        selectAllEnabled?: boolean | undefined
      }
    >
  }) => {
    const allSelected = section.data.every((sec) => sec.items.every((sp) => selected.includes(sp.value)))

    return (
      <Section>
        <Row>
          <SectionTitle>{section.title}</SectionTitle>
          {section.selectAllEnabled && (
            <Checkbox
              label={i18n.t('screens.edl.spaces.selectAll')}
              onClick={() =>
                onSelectAll(
                  allSelected,
                  section.data.reduce((acc, cur) => [...acc, ...cur.items.map((it) => it.value)], [] as string[])
                )
              }
              value={allSelected}
            />
          )}
        </Row>
      </Section>
    )
  }

  return (
    <SearchContainer>
      <InfoContainer>
        <ModalTitle>{title}</ModalTitle>
        <Current>{subtitle}</Current>

        <Close activeOpacity={0.8} onPress={() => Modal.close()}>
          <Icon name="close" size={24} />
        </Close>
      </InfoContainer>

      <ListContainer>
        <InputContainer>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={i18n.t('common.search')}
            left={{ name: 'search' }}
          />
        </InputContainer>

        <SectionList
          key={bp}
          sections={items}
          renderSectionHeader={renderSectionHeader}
          renderItem={renderSection}
          keyExtractor={(_, idx) => `${idx}`}
          extraData={selected}
          ItemSeparatorComponent={ModalSeparator}
        />
      </ListContainer>

      <InfoContainer>
        <ButtonContainer>
          <Button label={i18n.t('common.validate')} status={!!selected ? 'active' : 'disabled'} onClick={onSubmit} />
        </ButtonContainer>
      </InfoContainer>
    </SearchContainer>
  )
}

const SearchDrawer = ({ sections, onItemSelected, value, title }: Omit<Props, 'children'>) => {
  const i18n = useI18n()

  const [selected, setSelected] = React.useState(value)
  const [search, setSearch] = React.useState('')

  const onSelected = (val: string) => {
    const active = selected.includes(val)

    if (active) {
      setSelected(selected.filter((s) => s !== val))
    } else {
      setSelected([...selected, val])
    }
  }

  const onSelectAll = (allSelected: boolean, val: string[]) => {
    if (allSelected) {
      setSelected(selected.filter((s) => !val.includes(s)))
    } else {
      setSelected([...selected, ...val.filter((s) => !selected.includes(s))])
    }
  }

  const renderItem = ({ item }: { item: Option }) => {
    const focused = selected.includes(item.value)

    return (
      <Cell onPress={() => onSelected(item.value)} activeOpacity={0.6}>
        <Item>{item.label}</Item>
        {focused && <Icon name="check" color={theme.colors.primary} size={24} />}
      </Cell>
    )
  }

  const renderSectionHeader = ({ section }: { section: SpaceSection }) => {
    const allSelected = section.data.every((sp) => selected.includes(sp.value))

    return (
      <Section>
        <SectionTitle>{section.title}</SectionTitle>
        {section.selectAllEnabled && (
          <ChecklistContainer>
            <Checkbox
              label={i18n.t('screens.edl.spaces.selectAll')}
              onClick={() =>
                onSelectAll(
                  allSelected,
                  section.data.map((sp) => sp.value)
                )
              }
              value={allSelected}
              iconOnTheRight
            />
          </ChecklistContainer>
        )}
      </Section>
    )
  }

  const validate = () => {
    onItemSelected(selected)
    Drawer.close()
  }

  const items = React.useMemo(
    () =>
      sections.map((s) => ({
        ...s,
        data: s.data.filter((d) => sanitize(d.label).includes(sanitize(search))),
      })),
    [sections, search]
  )

  return (
    <MobileContainer>
      <Header>
        <SmallHeader onPress={() => Drawer.close()}>
          <Icon name="close" size={35} color={theme.colors.primary} />
        </SmallHeader>
        <CenterHeader>
          <DrawerTitle>{title}</DrawerTitle>
        </CenterHeader>
        <SmallHeader />
      </Header>
      <TopContainer>
        <TextInput
          onChange={setSearch}
          value={search}
          searchStyle="onLight"
          left={{ name: 'search' }}
          placeholder={i18n.t('screens.edl.spaces.placeholder')}
        />
      </TopContainer>
      <SectionList
        sections={items}
        renderSectionHeader={renderSectionHeader}
        renderItem={renderItem}
        ItemSeparatorComponent={DrawerSeparator}
        keyExtractor={(item) => item.value}
      />
      <Footer>
        <Button
          label={i18n.t('screens.incident.PRESTA.drawer.validate')}
          onClick={validate}
          status={!!selected ? 'active' : 'disabled'}
        />
      </Footer>
    </MobileContainer>
  )
}

const Search = ({ children, ...rest }: Props) => {
  const [, bp] = useBreakpoint()

  const openSearch = () => {
    if (bp !== 'phone') {
      Modal.open({
        Content: () => <SearchModal {...rest} />,
      })
    } else {
      Drawer.open({
        render: () => <SearchDrawer {...rest} />,
        hideTarget: true,
      })
    }
  }

  return (
    <TouchableOpacity activeOpacity={0.8} onPress={openSearch}>
      {children}
    </TouchableOpacity>
  )
}

const Service = {
  open: (props: Omit<Props, 'children'>, phone?: boolean) => {
    if (!phone) {
      Modal.open({
        Content: () => <SearchModal {...props} />,
      })
    } else {
      Drawer.open({
        render: () => <SearchDrawer {...props} />,
        hideTarget: true,
      })
    }
  },
  close: Modal.close,
}

export { Service }
export default Search

//MODAL
const SearchContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'width: 880px'};
`

const InfoContainer = styled(View)`
  padding: 24px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  position: relative;
`

const ModalTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 8px;
`

const Current = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? 4 : 24)}px;
  right: ${(props) => (props.theme.breakpoint === 'phone' ? 4 : 24)}px;
  padding: 5px;
`

const ListContainer = styled(View)`
  padding: 24px;
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 325 : 400)}px;
  background-color: ${(props) => props.theme.colors.background};
`

const InputContainer = styled(View)`
  margin-bottom: 24px;
`

const SelectContainer = styled(View)<{ half: boolean }>`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 400px;`}
  margin-right: ${(props) => (props.half ? 24 : 0)}px;
  margin-bottom: 16px;
`

const ModalSeparator = styled(View)`
  height: 24px;
`

const ButtonContainer = styled(View)`
  align-self: center;
  width: 275px;
`

const Section = styled(View)`
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.backgroundSecondary : 'transparent'};
  padding: 15px 16px;
`

const SectionTitle = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  ${(props) => props.theme.breakpoint !== 'phone' && `flex: 1;`}
`

//DRAWER
const TopContainer = styled(View)`
  padding: 0 21px 16px;
`

const Cell = styled(TouchableOpacity)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 0 16px;
  height: 45px;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`

const Item = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const DrawerSeparator = styled(View)`
  height: 1px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const Footer = styled(View)`
  margin: 16px;
`

const Header = styled(View)`
  flex-direction: row;
  padding: 21px;
`

const SmallHeader = styled(TouchableOpacity)`
  flex: 1;
`

const CenterHeader = styled(View)`
  flex: 4;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
`

const DrawerTitle = styled(Text)<{ top?: number }>`
  ${(props) => props.theme.fonts.Headline};
  margin-top: ${(props) => (props.top ? props.top : 0)}px;
`

const MobileContainer = styled(View)`
  flex: 1;
  padding-bottom: ${(props) => props.theme.insets.bottom}px;
`

const Items = styled(View)`
  flex-flow: row wrap;
`

const Row = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const ChecklistContainer = styled(View)`
  margin-top: 8px;
`
